import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { ScrollText, Check } from "lucide-react";

const Section = ({ n, title, children }) => (
  <div>
    <div className="font-bold text-slate-900 text-sm mt-3">{n}. {title}</div>
    <div className="text-[13px] text-slate-600 leading-relaxed space-y-1 mt-1">{children}</div>
  </div>
);
const Item = ({ children }) => <div className="flex gap-2"><span className="text-emerald-500">•</span><span>{children}</span></div>;

export default function TermsModal({ onAccept }) {
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/terms/accept");
      await onAccept();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4" data-testid="terms-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[88vh] flex flex-col shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center gap-2 shrink-0">
          <ScrollText className="text-emerald-600" size={20} />
          <div>
            <h2 className="font-display font-bold text-lg leading-none">Términos y Condiciones</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Sistema de Punto de Venta (POS)</p>
          </div>
        </div>

        <div className="px-6 py-4 overflow-y-auto text-[13px] text-slate-600 leading-relaxed" data-testid="terms-body">
          <p>Al contratar, renovar o utilizar el Sistema de Punto de Venta, el cliente acepta los siguientes términos y condiciones.</p>

          <Section n={1} title="Licencia y costos">
            <p>La licencia tiene un costo de <b>$2,000 MXN + IVA al año</b> e incluye:</p>
            <Item>Hasta 7 usuarios.</Item>
            <Item>Hasta 5 estaciones o terminales.</Item>
            <Item>Uso de las funciones contratadas.</Item>
            <Item>Actualizaciones generales del sistema.</Item>
            <Item>Soporte por fallas directamente relacionadas con el software.</Item>
            <p>La licencia tiene una vigencia de 12 meses a partir de su activación.</p>
            <p>Los adicionales tienen los siguientes costos:</p>
            <Item>Usuario adicional: $100 MXN + IVA al año.</Item>
            <Item>Estación adicional: $200 MXN + IVA al año.</Item>
          </Section>

          <Section n={2} title="Soporte técnico">
            <p>La licencia incluye soporte para errores o fallas atribuibles directamente al Sistema POS.</p>
            <p>No se consideran parte del soporte incluido los problemas ocasionados por equipos, Windows, internet, redes, drivers, configuraciones externas, virus o periféricos.</p>
          </Section>

          <Section n={3} title="Configuración de equipos y periféricos">
            <p>La instalación, configuración o asistencia relacionada con impresoras térmicas, básculas, cajones de dinero, lectores de códigos, impresoras de etiquetas, terminales u otros periféricos tendrá un costo adicional.</p>
            <p>No se garantiza compatibilidad con todos los modelos o marcas disponibles en el mercado. Se recomienda verificar la compatibilidad antes de adquirir un equipo.</p>
            <p>Contamos con técnicos capacitados para realizar instalaciones, configuraciones y diagnósticos, sujetos a cotización.</p>
          </Section>

          <Section n={4} title="Capacitaciones">
            <p>Las capacitaciones para el cliente o sus empleados no están incluidas en la licencia anual y tendrán un costo adicional.</p>
            <p>El soporte técnico está destinado a solucionar fallas del sistema y no sustituye una capacitación.</p>
          </Section>

          <Section n={5} title="Modificaciones y desarrollos">
            <p>Funciones especiales, modificaciones, reportes personalizados, integraciones o desarrollos solicitados por el cliente que no formen parte del sistema contratado podrán generar un costo adicional sujeto a cotización.</p>
            <p>Las nuevas funciones o módulos desarrollados posteriormente no necesariamente estarán incluidos en la licencia actual.</p>
          </Section>

          <Section n={6} title="Responsabilidad del cliente">
            <p>El cliente es responsable del uso adecuado del sistema, sus usuarios y contraseñas, así como de verificar la información capturada, incluyendo ventas, inventarios, precios, clientes, datos fiscales y movimientos de caja.</p>
            <p>No somos responsables por fallas ocasionadas por internet, energía eléctrica, equipos, redes, sistemas operativos, periféricos, servicios de terceros o configuraciones ajenas al sistema.</p>
          </Section>

          <Section n={7} title="Facturación y servicios externos">
            <p>Las funciones fiscales y de facturación deberán utilizarse con información correcta proporcionada por el cliente.</p>
            <p>Los servicios que dependan de terceros, como PAC, plataformas externas o autoridades fiscales, también estarán sujetos a su disponibilidad y condiciones.</p>
          </Section>

          <Section n={8} title="Renovación">
            <p>La licencia deberá renovarse cada 12 meses para mantener activo el servicio y soporte correspondiente.</p>
            <p>Los precios podrán actualizarse para futuras renovaciones, informando al cliente el costo vigente antes de realizar el pago.</p>
          </Section>

          <Section n={9} title="Aceptación">
            <p>La contratación, pago, activación, renovación o utilización del sistema implica la aceptación de estos términos y condiciones.</p>
          </Section>

          <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="font-bold text-slate-900 text-sm mb-2">Resumen del plan</div>
            <div className="space-y-1 text-[13px]">
              <div className="flex justify-between"><span>Licencia</span><b>$2,000 + IVA / año</b></div>
              <div className="flex justify-between"><span>Incluye</span><b>7 usuarios y 5 estaciones</b></div>
              <div className="flex justify-between"><span>Usuario adicional</span><b>$100 + IVA / año</b></div>
              <div className="flex justify-between"><span>Estación adicional</span><b>$200 + IVA / año</b></div>
              <div className="flex justify-between"><span>Soporte por fallas del sistema</span><b>Incluido</b></div>
              <div className="flex justify-between"><span>Configuración de periféricos</span><b>Costo adicional</b></div>
              <div className="flex justify-between"><span>Capacitaciones</span><b>Costo adicional</b></div>
              <div className="flex justify-between"><span>Servicios técnicos y desarrollos</span><b>Sujetos a cotización</b></div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border bg-slate-50 shrink-0 space-y-3">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
            <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} data-testid="terms-checkbox" className="w-4 h-4 accent-emerald-500" />
            He leído y acepto los Términos y Condiciones.
          </label>
          <Button disabled={!checked || busy} onClick={accept} data-testid="terms-accept-btn"
            className="w-full h-11 bg-emerald-500 hover:bg-emerald-600 text-white font-bold">
            <Check size={18} className="mr-1" /> {busy ? "Guardando…" : "Aceptar y continuar"}
          </Button>
        </div>
      </div>
    </div>
  );
}
